import { useAuthStore } from "@store";
import { AppError, toAppError } from "../types/errors";
import * as Sentry from "@sentry/react";
import { logDebug, logError } from "./logging";

/**
 * Global fetch interceptor that provides consistent error handling for all API calls
 * @param url The URL to fetch from
 * @param options Fetch options
 * @param serviceName The name of the calling service (for logging)
 * @param methodName The name of the calling method (for logging)
 * @returns Promise with the response
 */
export async function interceptedFetch(
  url: string,
  options: RequestInit = {},
  serviceName: string = "unknown",
  methodName: string = "unknown",
): Promise<Response> {
  const { jwt } = useAuthStore.getState();

  // Add authentication header if JWT is available and not already present
  const headers = new Headers(options.headers);
  if (jwt && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${jwt}`);
    logDebug(`interceptedFetch: Adding Authorization header with JWT ending in ...${jwt.slice(-6)}`);
  } else if (!jwt) {
    logDebug("interceptedFetch: No JWT found in store.");
  }

  // Add default content type for POST/PUT requests
  if (
    (options.method === "POST" ||
      options.method === "PUT" ||
      options.method === "PATCH") &&
    !headers.has("Content-Type")
  ) {
    headers.set("Content-Type", "application/json");
  }

  const requestOptions: RequestInit = {
    ...options,
    headers,
  };

  try {
    const response = await fetch(url, requestOptions);

    // Handle authentication and rate limiting errors globally
    if (!response.ok) {
      const errorData = await response
        .clone()
        .json()
        .catch(() => ({ detail: `Failed to fetch from ${methodName}` }));
      const errorMessage = errorData.detail || `HTTP error ${response.status}`;

      // Log to Sentry (production only)
      if (import.meta.env.PROD) {
        Sentry.captureMessage(
          `API Error in ${serviceName}.${methodName}: ${errorMessage}`,
          {
            contexts: {
              fetch_error: {
                status: response.status,
                url: response.url,
                response_body: errorData,
              },
            },
            level: "warning",
          },
        );
      }

      // Handle specific error types with Romanian messages
      if (response.status === 401) {
        const romanianMessage =
          "Sesiunea a expirat. Vă rugăm să vă autentificați din nou.";
        logDebug(`Authentication Error (401) in ${serviceName}.${methodName}.`);
        useAuthStore.getState().setAuthError(romanianMessage);
      } else if (response.status === 403) {
        const romanianMessage =
          "Nu aveți permisiunea de a accesa această resursă.";
        logDebug(
          `Authorization Error (403) in ${serviceName}.${methodName}. ${romanianMessage}`,
        );
        // We do NOT clear JWT on 403, as the token is still valid. Show error but keep auth state.
      } else if (response.status === 429) {
        const romanianMessage =
          "Ați atins limita de cereri. Vă rugăm să așteptați câteva momente și să încercați din nou.";
        logDebug(`Rate Limit Error (429) in ${serviceName}.${methodName}.`);
        useAuthStore.getState().setRateLimitError(true, romanianMessage);
      }

      logError(
        `Error in ${serviceName}.${methodName}:`,
        response.status,
        errorData,
      );
    }

    return response;
  } catch (error) {
    logError(`Network error in ${serviceName}.${methodName}:`, error);

    if (import.meta.env.PROD) {
      Sentry.captureException(error, {
        tags: { service: serviceName, method: methodName },
      });
    }

    // Re-throw network errors
    throw error;
  }
}

/**
 * Enhanced authenticated fetch that uses the global interceptor
 * @param url The full URL to fetch from
 * @param serviceName The name of the calling service (for logging)
 * @param methodName The name of the calling method (for logging)
 * @returns A promise that resolves to the JSON response data
 */
export async function authenticatedFetch<T>(
  url: string,
  serviceName: string,
  methodName: string,
): Promise<T> {
  const response = await interceptedFetch(
    url,
    { method: "GET" },
    serviceName,
    methodName,
  );

  if (!response.ok) {
    let errorBody: unknown = undefined;
    try {
      errorBody = await response.json();
    } catch {
      errorBody = { detail: `Failed to fetch from ${methodName}` };
    }
    const appError: AppError = toAppError(errorBody, response.status);
    throw appError;
  }

  return (await response.json()) as T;
}

/**
 * Enhanced POST request with global error handling
 * @param url The URL to post to
 * @param data The data to send
 * @param serviceName The name of the calling service (for logging)
 * @param methodName The name of the calling method (for logging)
 * @returns A promise that resolves to the JSON response data
 */
export async function authenticatedPost<T>(
  url: string,
  data: unknown,
  serviceName: string,
  methodName: string,
  init?: RequestInit,
): Promise<T> {
  const response = await interceptedFetch(
    url,
    {
      method: "POST",
      body: JSON.stringify(data),
      ...init,
    },
    serviceName,
    methodName,
  );

  if (!response.ok) {
    let errorBody: unknown = undefined;
    try {
      errorBody = await response.json();
    } catch {
      errorBody = { detail: `Failed to post to ${methodName}` };
    }
    const appError: AppError = toAppError(errorBody, response.status);
    throw appError;
  }

  return (await response.json()) as T;
}

/**
 * Enhanced PATCH request with global error handling
 * @param url The URL to patch
 * @param data The data to send
 * @param serviceName The name of the calling service (for logging)
 * @param methodName The name of the calling method (for logging)
 * @returns A promise that resolves to the JSON response data
 */
export async function authenticatedPatch<T>(
  url: string,
  data: unknown,
  serviceName: string,
  methodName: string,
): Promise<T> {
  const response = await interceptedFetch(
    url,
    {
      method: "PATCH",
      body: JSON.stringify(data),
    },
    serviceName,
    methodName,
  );

  if (!response.ok) {
    let errorBody: unknown = undefined;
    try {
      errorBody = await response.json();
    } catch {
      errorBody = { detail: `Failed to patch to ${methodName}` };
    }
    const appError: AppError = toAppError(errorBody, response.status);
    throw appError;
  }

  return (await response.json()) as T;
}

/**
 * Enhanced DELETE request with global error handling
 * @param url The URL to delete
 * @param serviceName The name of the calling service (for logging)
 * @param methodName The name of the calling method (for logging)
 * @returns A promise that resolves to the JSON response data (usually null or empty)
 */
export async function authenticatedDelete<T>(
  url: string,
  serviceName: string,
  methodName: string,
): Promise<T> {
  const response = await interceptedFetch(
    url,
    { method: "DELETE" },
    serviceName,
    methodName,
  );

  if (!response.ok) {
    let errorBody: unknown = undefined;
    try {
      errorBody = await response.json();
    } catch {
      errorBody = { detail: `Failed to delete from ${methodName}` };
    }
    const appError: AppError = toAppError(errorBody, response.status);
    throw appError;
  }

  // DELETE usually returns 204 No Content, which has no body.
  if (response.status === 204) {
    return null as unknown as T;
  }

  return (await response.json()) as T;
}
